/**
 * DrilldownPanel - Panel showing wells for selected period
 * @module wellRegistrationTotals/components/organisms/DrilldownPanel/DrilldownPanel
 */

import React from "react";
import { Box, Typography, CardContent, IconButton } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import CloseIcon from "@material-ui/icons/Close";
import { Card } from "../../../styled";
import { ExportButtons } from "../../molecules";
import { formatDrilldownTitle, getFilenameBase } from "../../../utils";
import Loader from "../../../../../../../components/Loader";
import DrilldownMap from "./DrilldownMap";
import DrilldownTable from "./DrilldownTable";

/**
 * Drilldown panel showing wells for a selected time period
 * @param {Object} props
 * @param {Object|null} props.selectedRow - Selected row data
 * @param {Function} props.onClose - Close handler
 * @param {Array} props.wells - Wells data
 * @param {boolean} props.isLoading - Loading state
 * @param {Object} [props.error] - Error object
 * @param {Function} props.onPrint - Print handler
 * @param {Function} props.onPngExport - PNG export handler
 * @param {Array} props.csvData - CSV export data
 * @param {Function} props.onMapViewChange - Map view change handler
 */
const DrilldownPanel = React.memo(
  ({
    selectedRow,
    onClose,
    wells = [],
    isLoading,
    error,
    onPrint,
    onPngExport,
    csvData,
    onMapViewChange,
  }) => {
    if (!selectedRow) return null;

    const title = formatDrilldownTitle(selectedRow.row_label);
    const filenameBase = getFilenameBase(selectedRow.row_label);
    const csvFilename = `registered-wells-${filenameBase}.csv`;

    return (
      <Card mt={4} className="no-print">
        <CardContent>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
            <ExportButtons
              csvData={csvData}
              csvFilename={csvFilename}
              onPrint={onPrint}
              onPngExport={onPngExport}
              disabled={!wells || wells.length === 0}
            />
          </Box>

          {error && (
            <Alert severity="error" style={{ marginBottom: 16 }}>
              Error loading wells: {error.message}
            </Alert>
          )}

          {isLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="100px"
            >
              <Loader />
            </Box>
          ) : (
            <>
              <Typography variant="h4" gutterBottom align="center">
                {title}
              </Typography>

              <Typography
                variant="subtitle2"
                gutterBottom
                align="center"
                color="textSecondary"
              >
                {wells.length} well{wells.length !== 1 ? "s" : ""} found
              </Typography>

              <DrilldownMap wells={wells} onViewChange={onMapViewChange} />

              <DrilldownTable wells={wells} />
            </>
          )}
        </CardContent>
      </Card>
    );
  }
);

DrilldownPanel.displayName = "DrilldownPanel";

export default DrilldownPanel;
